import React, { type FC } from 'react'

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Typography
} from '@mui/material'
import Button from '@mui/material/Button'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import {
  ImportDevicesForProvisionFileChangedAction,
  ImportDevicesForProvisionProfileIdChangedAction,
  SetImportDevicesDialogStateAction,
  uploadDevicesForProvisionCsv
} from '../../../redux/admin/admin-device-provisions-actions'
import { type RootState } from '../../../redux/store'

export const ImportDevicesForProvisionDialog: FC = () => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const importDevicesForProvisionFile = useAppSelector<File | null>((state: RootState) => state.adminDeviceProvisions.importDevicesForProvisionFile)
  const importDevicesForProvisionProfileId = useAppSelector<string>((state: RootState) => state.adminDeviceProvisions.importDevicesForProvisionProfileId)
  const importDevicesForProvisionDialogOpen = useAppSelector<boolean>((state: RootState) => state.adminDeviceProvisions.importDevicesForProvisionDialogOpen)

  const loadingDeviceProfiles = useAppSelector<boolean>((state: RootState) => state.adminDeviceProvisions.loadingDeviceProfiles)
  const deviceProfiles = useAppSelector<string[]>((state: RootState) => state.adminDeviceProvisions.deviceProfiles)
  const selectedTenant = useAppSelector<string>((state: RootState) => state.adminDeviceProvisions.selectedTenant)

  const closeDialog = (): void => {
    dispatch(SetImportDevicesDialogStateAction({ isOpen: false }))
  }

  const disabled = (): boolean => {
    return importDevicesForProvisionFile == null || importDevicesForProvisionProfileId === ''
  }

  const onImport = (): void => {
    const file = importDevicesForProvisionFile
    if (file == null) {
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    formData.append('profileId', importDevicesForProvisionProfileId)
    formData.append('tenantName', selectedTenant)
    dispatch(uploadDevicesForProvisionCsv(formData, t))
      .catch(err => {
        console.error('upload devices for provision error: ', err)
      })
  }

  const onFileChange = (event): void => {
    const target = event.target as HTMLInputElement
    if (target?.files == null || target.files.length === 0) {
      return
    }

    const file = Array.from(target.files)[0]
    dispatch(ImportDevicesForProvisionFileChangedAction({ file }))
  }

  const onProfileIdChange = (profileId: string): void => {
    dispatch(ImportDevicesForProvisionProfileIdChangedAction({ profileId }))
  }

  return (
        <Dialog
            open={importDevicesForProvisionDialogOpen}
            maxWidth={'sm'}
            fullWidth
        >
            <DialogTitle>{t('settings_dialog.import_meters_to_activate')}</DialogTitle>
            <DialogContent>
                <Grid item container>
                    <Grid item xs>
                      <Typography variant="body1" style={{ marginTop: '1rem' }}>
                        {t('settings_dialog.selected_tenant')}:
                      </Typography>
                      <Typography variant="body1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
                        {selectedTenant}
                      </Typography>
                        <Button
                            variant='outlined'
                            component='label'
                            color='primary'
                        >
                            {t('select_csv_file')}
                            <input id='csv-file-input' type='file' style={{ display: 'none' }}
                                   onChange={onFileChange}/>
                        </Button>
                        {importDevicesForProvisionFile != null &&
                          <div style={{ marginTop: '1rem', fontSize: '14px' }}>{`${t('selected_file')}: ${importDevicesForProvisionFile.name}`}</div>}
                    </Grid>
                </Grid>
                <Grid item container>
                    <Grid item xs>
                        {loadingDeviceProfiles &&
                          <span>{t('settings_dialog.downloading_profiles')}</span>}
                        {!loadingDeviceProfiles && (
                            <FormControl sx={{ minWidth: 140, marginTop: '3rem' }}>
                              <InputLabel>{t('settings_dialog.select_profile')}</InputLabel>
                                <Select
                                    sx={{ minWidth: 160 }}
                                    value={importDevicesForProvisionProfileId}
                                    onChange={(event) => {
                                      onProfileIdChange(event.target.value)
                                    }}
                                    label={t('settings_dialog.select_profile')}
                                >
                                    {deviceProfiles.map((profile) => <MenuItem key={profile} value={profile}>{profile}</MenuItem>)}
                                </Select>
                            </FormControl>
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button key='cancel' color='primary' onClick={() => {
                  closeDialog()
                }}>{t('common.actions.cancel')}</Button>,
                <Button key='import' color='secondary' disabled={disabled()} onClick={() => {
                  onImport()
                }}>{t('settings_dialog.import')}</Button>
            </DialogActions>
        </Dialog>
  )
}
